import i18n from '@/src/i18n';
import { LeaderboardEntry } from '@/src/types/JamReport';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { getLocaleTimelineFormattedDate } from '@/src/utils/time.utils';
import { Container, LineChart, MixedLineBarChartProps } from '@amzn/awsui-components-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const FIVE_MINUTES_IN_MS = 1000 * 60 * 5;

type LineDataSeries<T> = MixedLineBarChartProps.LineDataSeries<T>;
type ThresholdSeries<T> = MixedLineBarChartProps.ThresholdSeries<T>;
type LineSeries<T> = LineDataSeries<T> | ThresholdSeries<T>;

interface ScoreEntry {
  timestamp: number;
  score: number;
}

export interface ScoringTrendsProps {
  eventStartDate: Date;
  leaderboardEntries: LeaderboardEntry[];
}

export const ScoringTrends: React.FC<ScoringTrendsProps> = (props) => {
  const { t } = useTranslation();

  const series = useMemo(() => {
    const teamData: Record<string, ScoreEntry[]> = {};
    const teamNames: Record<string, string> = {};

    // Build team names mapping
    for (const entry of props.leaderboardEntries) {
      if (!entry.teamName) {
        continue;
      }

      teamNames[entry.teamName] = entry.teamDisplayName || entry.teamName;
    }

    // Process entries
    for (const entry of props.leaderboardEntries) {
      if (!entry.teamName) {
        continue;
      }

      const teamName = teamNames[entry.teamName];

      if (!teamData[teamName]) {
        const startEntry = {
          timestamp: props.eventStartDate.getTime(),
          score: 0,
        };

        teamData[teamName] = [startEntry];
      }

      if (!entry.timestamp) {
        continue;
      }

      teamData[teamName].push({
        timestamp: entry.timestamp,
        score: entry.teamTotalEffectiveScore,
      });
    }

    const data: LineSeries<Date>[] = [];
    for (const [teamDisplayName, entries] of Object.entries(teamData)) {
      entries.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

      data.push({
        type: 'line',
        title: teamDisplayName,
        data: entries.map((entry) => ({
          x: new Date(entry.timestamp || 0),
          y: entry.score,
        })),
      });
    }

    data.push({
      type: 'threshold',
      title: t(i18nKeys.jamDashboard.leaderboard.table.threshold.eventStart),
      x: props.eventStartDate,
    } as ThresholdSeries<Date>);

    return data;
  }, [props.leaderboardEntries]);

  const startTime = props.eventStartDate.getTime() - FIVE_MINUTES_IN_MS;
  let endTime = props.eventStartDate.getTime() + FIVE_MINUTES_IN_MS;
  let yMax = 10;

  for (const entry of props.leaderboardEntries) {
    endTime = Math.max(endTime, entry.timestamp || 0);
    yMax = Math.max(yMax, entry.teamTotalEffectiveScore);
  }

  return (
    <Container>
      <LineChart
        series={series}
        xDomain={[new Date(startTime), new Date(endTime)]}
        xTickFormatter={(date) => getLocaleTimelineFormattedDate(date, i18n.language)}
        xTitle={t(i18nKeys.jamDashboard.leaderboard.table.headers.time)}
        yTitle={t(i18nKeys.jamDashboard.leaderboard.table.headers.score)}
        xScaleType="time"
        yDomain={[0, yMax + 5]}
        hideFilter
      />
    </Container>
  );
};
